import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useEffectEvent } from 'react';
import { useAuth } from '@/lib/authClient';
import { useDealsSelectedDeal } from '@/stores/dealsStore';
import { useFilterStoreFilters, useFilterStoreSearchTerm, useFilterStoreSortBy } from '@/stores/filterStore';
import { useLocation } from '@/stores/locationStore';
import { useMarkerIoActions, useMarkerIoLoaded } from '@/stores/markerIoStore';
import { useSettings } from '@/stores/settingsStore';

interface MarkerIoContextType {
  loaded: boolean;
  loadMarkerSDK: () => Promise<void>;
  unloadMarkerSDK: () => void;
}

const MarkerIoContext = createContext<MarkerIoContextType | null>(null);

interface MarkerIoProviderProps {
  children: ReactNode;
}

export function MarkerIoProvider({ children }: MarkerIoProviderProps) {
  const {
    loadMarkerSDK,
    unloadMarkerSDK,
    updateReporterInfo,
    updateMarkerIoDealData,
    updateMarkerIoFilters,
    updateMarkerIoSettings,
    updateMarkerIoLocation,
  } = useMarkerIoActions();
  const loaded = useMarkerIoLoaded();
  const selectedDeal = useDealsSelectedDeal();
  const userLocation = useLocation();
  const filters = useFilterStoreFilters();
  const searchTerm = useFilterStoreSearchTerm();
  const sortBy = useFilterStoreSortBy();
  const settings = useSettings();
  const { user } = useAuth();

  const ensureLoaded = useEffectEvent(() => {
    if (!loaded) {
      loadMarkerSDK();
    }
  });

  const ensureUnloaded = useEffectEvent(() => {
    if (loaded) {
      unloadMarkerSDK();
    }
  });

  useEffect(() => {
    ensureLoaded();

    return () => {
      ensureUnloaded();
    };
  }, []);

  useEffect(() => {
    if (user && loaded) {
      updateReporterInfo({
        fullName: user.name,
        email: user.email,
      });
    }
  }, [user, loaded, updateReporterInfo]);

  useEffect(() => {
    if (loaded) {
      updateMarkerIoDealData(selectedDeal);
    }
  }, [selectedDeal, loaded, updateMarkerIoDealData]);

  useEffect(() => {
    if (filters && loaded) {
      updateMarkerIoFilters(filters, searchTerm, sortBy);
    }
  }, [filters, loaded, searchTerm, sortBy, updateMarkerIoFilters]);

  useEffect(() => {
    if (settings && loaded) {
      updateMarkerIoSettings(settings);
    }
  }, [settings, loaded, updateMarkerIoSettings]);

  useEffect(() => {
    if (userLocation && loaded) {
      updateMarkerIoLocation(userLocation);
    }
  }, [userLocation, loaded, updateMarkerIoLocation]);

  const contextValue: MarkerIoContextType = {
    loaded,
    loadMarkerSDK,
    unloadMarkerSDK,
  };

  return <MarkerIoContext.Provider value={contextValue}>{children}</MarkerIoContext.Provider>;
}

export function useMarkerIo() {
  const context = useContext(MarkerIoContext);
  if (!context) {
    throw new Error('useMarkerIo must be used within a MarkerIoProvider');
  }
  return context;
}
